import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import getContentAreaFromSpread from 'editor/src/store/design/util/getContentAreaFromSpread';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import getFocusedContent from 'editor/src/store/editor/selector/getFocusedContent';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import { DEFAULT_MAX_TOTAL_COPIES } from 'editor/src/store/gangSheet/constants';
import getResolvedAllowRotate from 'editor/src/store/gangSheet/selector/getResolvedAllowRotate';
import getResolvedGapMm from 'editor/src/store/gangSheet/selector/getResolvedGapMm';
import getResolvedMarginMm from 'editor/src/store/gangSheet/selector/getResolvedMarginMm';
import getArtworkGroups from 'editor/src/store/gangSheet/util/getArtworkGroups';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import nestOnSheet, { NestResult } from 'editor/src/util/gangSheet/nestOnSheet';

import autoNestOperation from './autoNestOperation';
import setArtworkQtyOperation from './setArtworkQtyOperation';

// "Fill sheet" for one artwork group: compute how many copies of it fit alone on the
// sheet at the group's size (respecting the total-copies cap left by the other groups),
// set that as the group's canvas copy count, then run the regular auto nest for
// everything on the sheet. A single undo step.
const fillSheetWithArtworkOperation =
  (groupKey: string) =>
  (dispatch: ThunkDispatch, getState: () => RootState): NestResult | undefined => {
    const state = getState();
    const gangSheetSettings = state.hostSettings.gangSheet;
    if (!gangSheetSettings) {
      return undefined;
    }

    const spreadIndex = getCurrentSpreadIndex(state);
    const focusedContent = getFocusedContent(state);
    const area = getContentAreaFromSpread(state.design.designData?.spreads[spreadIndex], focusedContent);
    if (!area) {
      return undefined;
    }

    const groups = getArtworkGroups(state.design.designData?.spreads[spreadIndex]);
    const group = groups.find((candidate) => candidate.key === groupKey);
    if (!group) {
      return undefined;
    }

    const gapMm = getResolvedGapMm(state) ?? gangSheetSettings.defaultGapMm;
    const allowRotate = getResolvedAllowRotate(state) ?? gangSheetSettings.allowRotate;
    const marginMm = getResolvedMarginMm(state);
    const maxTotalCopies = gangSheetSettings.maxTotalCopies ?? DEFAULT_MAX_TOTAL_COPIES;

    // the cap left after the other groups' copies
    const otherCopies = groups.reduce(
      (sum, candidate) => (candidate.key === groupKey ? sum : sum + candidate.elements.length),
      0,
    );
    const remainingCap = Math.max(0, maxTotalCopies - otherCopies);

    const aloneResult = nestOnSheet(
      { widthMm: area.width, heightMm: area.height },
      [
        {
          id: groupKey,
          widthMm: group.widthMm,
          heightMm: group.heightMm,
          qty: remainingCap,
        },
      ],
      { gapMm, allowRotate, marginMm },
    );

    // never drop the artwork entirely — an oversized artwork keeps its one copy
    const fitCount = Math.max(1, aloneResult.placed.length);

    let result: NestResult | undefined;

    batch(() => {
      dispatch(saveUndoRedoStateOperation('Fill sheet'));
      dispatch(setArtworkQtyOperation(groupKey, fitCount, { saveUndoRedoStep: false }));
      result = dispatch(autoNestOperation({ saveUndoRedoStep: false }));
    });

    return result;
  };

export default fillSheetWithArtworkOperation;
